import { useEffect } from "react";
import { useState } from "react";
import { useIntl } from "react-intl";
import label from "../../../../../i18n/label";
import SendIcon from "../../../../../assets/icons/SendIcon";
import Spinner from "../../../../../assets/icons/Spinner";
import Message from "../../../../../i18n/Message";

import "./PromptTextarea.scss";
import promptControlViewType from "../promptControlViewType";
import backend from "../../../../../backend/backend";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import Notify from "../../../../shared/Notify";
import ChatActions from "../../store/chatActions";
import { useParams } from "react-router-dom";
import UploadIcon from "../../../../../assets/icons/UploadIcon";
import { scrollToChatBottom } from "../../details/chatScrollUtils";
import PluginSelector from "./PluginSelector";
import KnowledgeBaseSelector from "./KnowledgeBaseSelector";
import { toolPluginSelectors } from "../../../tools/store/toolPluginReducer";
import {
  loadToolPluginSettings,
  saveToolPluginSettings,
} from "../../../tools/store/toolPluginActionCreators";

const PromptTextarea = ({ view, onSubmit, assistant }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { workspaceId, chatId } = useParams();
  const [formElem, setFormElem] = useState(null);
  const [input, setInput] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitOnEnter, setSubmitOnEnter] = useState(true);
  const [textareaStyles, setTextAreaStyles] = useState({ height: "56px" });
  const [isUploading, setIsUploading] = useState(false);
  const [selectedKBases, setSelectedKBases] = useState([]);
  const [assistantPluginsInitialized, setAssistantPluginsInitialized] =
    useState(false);
  const [assistantKBasesInitialized, setAssistantKBasesInitialized] =
    useState(false);

  const toolPluginSettings = useSelector(
    toolPluginSelectors.getToolPluginSettings
  );
  //console.log("toolPluginSettings", toolPluginSettings);
  const hidden = view !== promptControlViewType.TEXTAREA;

  // Check if assistant has plugins or knowledge bases
  const hasAssistantData =
    assistant &&
    ((assistant.toolPluginUids && assistant.toolPluginUids.length > 0) ||
      (assistant.knowledgeBaseUids && assistant.knowledgeBaseUids.length > 0));
  const isReadOnly = !!hasAssistantData;
  useEffect(() => {
    if (input && !loading && !hidden) {
      input.focus();
    }
  }, [input, loading, hidden]);

  useEffect(() => {
    if (workspaceId) {
      dispatch(loadToolPluginSettings(intl, workspaceId));
    }
  }, [dispatch, intl, workspaceId]);

  // Reset initialization flags when chat changes or assistant becomes null
  useEffect(() => {
    setAssistantPluginsInitialized(false);
    setAssistantKBasesInitialized(false);
  }, [chatId, assistant?.uid]);

  // Preselect assistant's plugins (only once when assistant is loaded)
  useEffect(() => {
    if (
      assistant &&
      assistant.toolPluginUids &&
      assistant.toolPluginUids.length > 0 &&
      !assistantPluginsInitialized
    ) {
      dispatch(
        saveToolPluginSettings(intl, workspaceId, assistant.toolPluginUids)
      );
      setAssistantPluginsInitialized(true);
    } else if (!assistant && !assistantPluginsInitialized) {
      // If no assistant, mark as initialized to prevent loading
      setAssistantPluginsInitialized(true);
    }
  }, [
    assistant?.uid,
    assistant?.toolPluginUids,
    assistantPluginsInitialized,
    dispatch,
    intl,
    workspaceId,
  ]);

  // Preselect assistant's knowledge bases (only once when assistant is loaded)
  useEffect(() => {
    if (
      assistant &&
      assistant.knowledgeBaseUids &&
      assistant.knowledgeBaseUids.length > 0 &&
      !assistantKBasesInitialized
    ) {
      setSelectedKBases(assistant.knowledgeBaseUids);
      setAssistantKBasesInitialized(true);
    } else if (!assistant && !assistantKBasesInitialized) {
      // If no assistant, mark as initialized to prevent loading
      setAssistantKBasesInitialized(true);
    }
  }, [
    assistant?.uid,
    assistant?.knowledgeBaseUids,
    assistantKBasesInitialized,
  ]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections && fileRejections.length > 0) {
        Notify.error(label(intl, "error.invalid_files_dropped"));
        return;
      }
      onFilesUpload(acceptedFiles);
    },
    maxSize: 100 * 1024 * 1024,
    noClick: true,
    noKeyboard: true,
    accept: {
      "application/pdf": [".pdf"],
      "application/rtf": [".rtf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/csv": [".csv"],
      /*"application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],*/
      "text/plain": [".txt"],
    },
  });

  const onFilesUpload = (files) => {
    console.log("Uploading: ", files);
    setIsUploading(true);

    backend
      .uploadChatFile(workspaceId, chatId, files[0])
      .then((resp) => {
        setIsUploading(false);
        dispatch({
          type: ChatActions.MESSAGE_FILE_UPLOADED,
          message: resp,
        });
        setTimeout(scrollToChatBottom, 250);
        // dispatch(loadWorkspaceFonts(intl, workspaceId));
      })
      .catch(() => {
        Notify.error(label(intl, "error_file_upload"));
        setIsUploading(false);
      });
    /*
    setIsUploading(true);
    const requests = files.map((file, index) => {
      return backend.createWorkspaceFont(workspaceId, file);
    });
    Promise.all(requests)
      .then(() => {
        setIsUploading(false);
        dispatch(loadWorkspaceFonts(intl, workspaceId));
      })
      .catch(() => {
        Notify.error(label(intl, "error_font_upload"));
        setIsUploading(false);
      });
      */
  };

  window.gAppendPromptToMessage = (item) => {
    setContent((prev) => {
      const newValue = (prev ? prev + " " : "") + item.content;
      if (newValue.length >= 105) {
        setTimeout(() => {
          const elem = document.querySelector(".prompt_textarea");
          setTextAreaStyles((prev) => {
            return {
              ...prev,
              overflow: "hidden",
              height: elem.scrollHeight + 2 + "px",
            };
          });
        }, 150);
      }
      return newValue;
    });
  };

  const onSendClicked = () => {
    if (loading) return;
    if (content) {
      setLoading(true);

      const onFinish = () => {
        setLoading(false);
      };

      onSubmit(
        content,
        { pluginUids: toolPluginSettings, kbaseUids: selectedKBases },
        onFinish,
        onFinish
      );
      setContent("");
    } else {
      input.checkValidity();
      input.reportValidity();
    }
  };
  const onFormSubmit = (e) => {
    e.preventDefault();
    onSendClicked();
  };
  const onKeyUp = (e) => {
    const elem = e.target;
    const currentHeight = parseInt(elem.style.height);
    console.log(
      "Element height now = ",
      currentHeight,
      " with scroll: ",
      e.target.scrollHeight,
      e.target.clientHeight
    );

    if (e.target.scrollHeight <= 220) {
      setTextAreaStyles({
        ...textareaStyles,
        overflow: "hidden",
        height: e.target.scrollHeight + 2 + "px",
      });
      /*
      if (
        currentHeight !== elem.scrollHeight + 1 &&
        currentHeight + 2 !== elem.scrollHeight
      ) {
        console.log("Appluing height ", e.target.scrollHeight, new Date());
        elem.style.height = "auto";
        elem.style.height = elem.scrollHeight + 2 + "px";
        setTextAreaStyles({
          ...textareaStyles,
          overflow: "hidden",
          height: e.target.scrollHeight + 2 + "px",
        });
      }
      */
    } else {
      setTextAreaStyles({
        ...textareaStyles,
        overflow: "auto",
      });
    }
  };
  const onKeyDown = (e) => {
    if (submitOnEnter && e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      onSendClicked();
    }
  };
  return (
    <div className={"prompt_textarea " + (hidden ? "hidden_view" : "")}>
      <div
        className={
          "dropzone-wrapper " + (isDragActive ? "dropzone-drag-active" : "")
        }
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <form onSubmit={onFormSubmit} ref={(val) => setFormElem(val)}>
          <textarea
            ref={(val) => setInput(val)}
            autoFocus={true}
            disabled={loading}
            onChange={(e) => setContent(e.target.value)}
            onKeyUp={onKeyUp}
            onKeyDown={onKeyDown}
            style={textareaStyles}
            value={content}
            required
            placeholder={label(intl, "prompt_text_placeholder")}
            className="form-control prompt_textarea"
          ></textarea>
        </form>
        <div className="bottom_row">
          <div className="left_panel prompt_textarea_actions_toolbar">
            {!loading && (
              <>
                <div
                  className="prompt_textarea_toolbar_action"
                  onClick={() => !isUploading && open()}
                >
                  {!isUploading && <UploadIcon color="#344767" size={20} />}
                  {isUploading && <Spinner color="#344767" size={20} />}
                  {/*<Message id="chat_toolbar_upload" />*/}
                </div>

                <PluginSelector
                  selectedPlugins={toolPluginSettings}
                  onPluginsChange={(plugins) => {
                    if (!isReadOnly) {
                      dispatch(
                        saveToolPluginSettings(intl, workspaceId, plugins)
                      );
                    }
                  }}
                  isReadOnly={isReadOnly}
                />

                <KnowledgeBaseSelector
                  selectedKBases={selectedKBases}
                  onKBasesChange={(kbases) => {
                    if (!isReadOnly) {
                      setSelectedKBases(kbases);
                    }
                  }}
                  isReadOnly={isReadOnly}
                />
              </>
            )}
          </div>
          <div className="right_panel options_panel">
            <div className="submit_on_enter">
              <input
                type="checkbox"
                checked={submitOnEnter}
                onChange={() => setSubmitOnEnter(!submitOnEnter)}
              />
              <div onClick={() => setSubmitOnEnter(!submitOnEnter)}>
                <Message id="submit_on_enter" />
              </div>
            </div>
            <div
              className={"send_button " + (loading ? "loading" : "")}
              onClick={onSendClicked}
            >
              {loading ? <Spinner /> : <SendIcon size={18} color="#fff" />}
              <Message id="prompt_control_send" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromptTextarea;
